/**
 * Build library payloads (and their clipboard envelopes) from live config
 * parts — the write-side mirror of parse-library-json.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  CapacityRule,
  ExcludeRulesTree,
  FormulaConfig,
} from '@trafficpulse/shared';

import {
  FORMULA_CLIPBOARD_KIND,
  FORMULA_CLIPBOARD_VERSION,
} from '@/features/flow-optimizer/clipboard/formula-clipboard.types';
import { buildFormulaClipboardPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.serialize';
import type { FormulaConfigPayload } from '@/features/flow-optimizer/clipboard/formula-clipboard.types';

import type { PackagePayload, RuleSetPayload } from './library-payload.types';

export function buildRuleSetPayload(
  excludeRules: ExcludeRulesTree,
  capacityRules: CapacityRule[] = [],
): RuleSetPayload {
  return {
    excludeRules: {
      paths: excludeRules.paths ?? null,
      landers: excludeRules.landers ?? null,
      offers: excludeRules.offers ?? null,
    },
    capacityRules,
  };
}

export function buildFormulaSetPayload(
  formula: FormulaConfig,
): FormulaConfigPayload {
  // Reuse the formula clipboard serializer — per-layer weights, no ids.
  return buildFormulaClipboardPayload(formula).data as FormulaConfigPayload;
}

export function buildPackagePayload(opts: {
  name?: string;
  formula: FormulaConfig;
  excludeRules: ExcludeRulesTree;
  capacityRules?: CapacityRule[];
}): PackagePayload {
  return {
    ...(opts.name ? { name: opts.name } : {}),
    formula: buildFormulaSetPayload(opts.formula),
    rules: buildRuleSetPayload(opts.excludeRules, opts.capacityRules ?? []),
  };
}

/** Envelope wrapper shared by copy features and the AI JSON contract. */
export function wrapLibraryPayload(
  type: 'rule-set' | 'package',
  label: string,
  data: RuleSetPayload | PackagePayload,
): Record<string, unknown> {
  return {
    kind: FORMULA_CLIPBOARD_KIND,
    version: FORMULA_CLIPBOARD_VERSION,
    type,
    copiedAt: new Date().toISOString(),
    label,
    data,
  };
}
